import { useRef, useState } from "react";
import clsx from "clsx";
import { createLink } from "@tanstack/react-router";
import { bannersPlacementQueryOptions } from "@/app/lib/hooks/useBannersPlacement";
import { useInViewportOnce } from "@/app/lib/hooks/useInViewportOnce";
import { Image } from "@/app/ui/Image";
import { rudderAnalytics, ContentTrackingEvents } from "@/app/lib/rudderstack";
import type {
  PublicBannerData,
  PublicPlacementData,
} from "@/app/lib/types/codegen";
import type { BannersPlacementSlug } from "@/app/lib/types/enums";
import { Skeleton } from "@mui/material";
import { Slider } from "../Slider";
import { ContentBlockDrawer } from "./ContentBlockDrawer";
import { useQuery } from "@tanstack/react-query";

const ImageLink = createLink(Image);

type BannersPlacementProps = {
  slug: BannersPlacementSlug;
  classNames?: {
    wrapper?: string;
  };
};

export function BannersPlacement({ slug, classNames }: BannersPlacementProps) {
  const { data: placement, isPending } = useQuery(
    bannersPlacementQueryOptions(slug),
  );

  if (isPending) {
    return (
      <div className="w-full">
        <Skeleton
          variant="text"
          width={150}
          className="mx-4 text-xl"
          sx={{
            mb: 2,
            bgcolor: "rgba(0, 0, 0, 0.1)",
          }}
        />

        <Slider
          items={[1, 2, 3]}
          getItemKey={(item) => item}
          slideClassName="w-9/10 min-w-9/10 rounded-2xl"
          renderItem={() => (
            <Skeleton
              data-testid="banner-skeleton"
              variant="rounded"
              className="aspect-[16/9] w-full"
              height="unset"
              sx={{
                bgcolor: "rgba(0, 0, 0, 0.1)",
              }}
            />
          )}
        />
      </div>
    );
  }

  if (!placement || !placement.isEnabled || placement.banners.length === 0) {
    return null;
  }

  return (
    <div className={classNames?.wrapper}>
      <PlacementContent placement={placement} />
    </div>
  );
}

type PlacementContentProps = {
  placement: PublicPlacementData;
};

function PlacementContent({ placement }: PlacementContentProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<PublicBannerData | null>(
    null,
  );

  const handleBannerClick = (banner: PublicBannerData, position: number) => {
    rudderAnalytics.track(ContentTrackingEvents.CONTENT_BANNER_CLICKED, {
      placement: placement.slug,
      banner_name: banner.name,
      banner_image_url: banner.imageUrl,
      banner_position: position,
    });

    if (banner.actionType !== "DRAWER" || !banner.contentBlock) return;

    setIsDrawerOpen(true);
    setSelectedBanner(banner);
  };

  const handleContentButtonClick = (buttonText: string) => {
    if (!selectedBanner?.contentBlock) return;

    rudderAnalytics.track(ContentTrackingEvents.CONTENT_BLOCK_BUTTON_CLICKED, {
      placement: placement.slug,
      banner_name: selectedBanner.name,
      content_block: selectedBanner.contentBlock.title,
      button_text: buttonText,
    });
  };

  useInViewportOnce(containerRef, {
    callback: () => {
      rudderAnalytics.track(ContentTrackingEvents.CONTENT_PLACEMENT_VIEWED, {
        placement: placement.slug,
        banner_count: placement.banners.length,
      });
    },
  });

  const banners = placement.banners;
  const sectionTitle = placement.sectionTitle;
  const hasMultipleBanners = banners.length > 1;

  return (
    <div ref={containerRef} className="max-w-full">
      {!!sectionTitle && (
        <h6 className="mb-4 px-4 text-xl font-bold text-text-title">
          {sectionTitle}
        </h6>
      )}

      <div
        className={clsx({
          "px-4": !hasMultipleBanners,
        })}
      >
        <Slider
          items={banners}
          getItemKey={(banner, index) => `${banner.id}-${index}`}
          slideClassName="w-9/10 min-w-9/10 rounded-2xl"
          renderItem={(banner, index) => {
            const isLinkAction = banner.actionType === "LINK";

            let Component: typeof Image | typeof ImageLink = Image;
            if (isLinkAction) {
              Component = ImageLink;
            }

            return (
              <Component
                src={banner.imageUrl}
                alt={banner.altText}
                className="aspect-[16/9] w-full object-cover"
                fetchPriority={index === 0 ? "high" : "auto"}
                onClick={() => handleBannerClick(banner, index)}
                {...(isLinkAction && {
                  to: banner.linkUrl,
                })}
              />
            );
          }}
        />
      </div>

      <ContentBlockDrawer
        open={isDrawerOpen && !!selectedBanner}
        onClose={() => setIsDrawerOpen(false)}
        contentBlock={selectedBanner?.contentBlock ?? undefined}
        onButtonClick={handleContentButtonClick}
      />
    </div>
  );
}
